import { readStoredClubId } from "../lib/clubSelection";
import { useAbortableConfirm } from "../lib/useAbortableConfirm";
import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";

import { useAuth } from "../auth/AuthContext";
import {
  api,
  ApiError,
  matchParticipantShortName,
  pendingSideLabel,
  type ClubMember,
  type ClubReservationInfo,
  type CourtBlock,
  type TournamentMatch,
} from "../lib/api";
import CourtTimeGrid, {
  CourtTimeGridBar,
  type CourtTimeGridCell,
  type CourtTimeGridItemInfo,
} from "../components/CourtTimeGrid";
import CalendarDateField from "../components/CalendarDateField";
import { sportLabel } from "../lib/labels";
import {
  isActiveCalendarStatus,
  isBulkProgrammableCalendarStatus,
  isSchedulePublished,
  isScheduleUnpublished,
} from "../lib/matchScheduleStatus";
import { hasKnownPlayer, horariosText, scheduleBarConsequence } from "../lib/schedulePublish";
import {
  buildGridRange,
  calendarTimeOptionsFor,
  durationForServer,
  friendlyTime12h,
  intervalLabel,
  matchGridInterval,
  parseTimeMinutes,
  startAvailability,
  steppedStarts,
  timeFromMinutes,
  venueKey,
  type CourtGridItem,
} from "../lib/scheduleGrid";
import { validDurationMinutes } from "../lib/calendarSettings";
import { courtBase, courtDisplayName, courtsFromCount, courtVisibleIn } from "../lib/courts";
import { feedersFirstQueue } from "../lib/roundOrderQueue";

type CalendarOrderMode = "manual" | "random" | "tournament";
type CalendarInteractionMode = "schedule" | "block" | "reserve";

/** Lo que pinta una tarjeta del grid de canchas del club. */
type ClubGridPayload =
  | { type: "match"; match: TournamentMatch }
  | { type: "block"; block: CourtBlock }
  | { type: "reservation"; reservation: ClubReservationInfo };
type ClubGridItem = CourtGridItem<ClubGridPayload>;

/** Para revisar si cabe un partido de `minutes`: los partidos cuya categoría
 * no tiene bloque guardado (sin `duration_minutes`) se asumen tan largos como
 * él, igual que el servidor al revisar choques (revisión 2026-10-01). */
function clubItemsForPlacing(items: ClubGridItem[], minutes: number): ClubGridItem[] {
  return items.map((item) =>
    item.payload.type === "match" && validDurationMinutes(item.payload.match.duration_minutes) === null
      ? { ...item, endMin: item.startMin + Math.max(1, minutes) }
      : item,
  );
}

function matchPlayerName(brief: TournamentMatch["player1"]): string {
  if (!brief) return "Bye";
  return brief.display_name ?? `Jugador #${brief.player_profile_id}`;
}

/** `short`: nombres cortos («Ana / Beto» en dobles) para tarjetas de media
 * columna (mini canchas). */
function matchShortLabel(match: TournamentMatch, options: { short?: boolean } = {}): string {
  // Placeholder del esqueleto (sin resultado): lado vacío = "Por definir".
  const isPendingSlot = !match.winner_participant_id && !match.score_text;
  const side = (brief: TournamentMatch["player1"], sideNumber: 1 | 2) =>
    brief
      ? options.short
        ? matchParticipantShortName(brief)
        : matchPlayerName(brief)
      : isPendingSlot
        ? pendingSideLabel(match.notes, sideNumber)
        : "Bye";
  return `${side(match.player1, 1)} vs ${side(match.player2, 2)}`;
}

function todayIso(): string {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}-${String(now.getDate()).padStart(2, "0")}`;
}

/** Día anterior/siguiente de una fecha YYYY-MM-DD (flechas del día). */
function shiftIsoDate(iso: string, days: number): string {
  const [year, month, day] = iso.split("-").map(Number);
  if (!year || !month || !day) return iso;
  const date = new Date(year, month - 1, day + days);
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`;
}

/** Texto sin acentos ni mayúsculas para el buscador de la cola. */
function normalizeQueueText(value: string): string {
  return value.normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase();
}

function blockLabel(block: CourtBlock): string {
  return `${block.start_time}–${block.end_time} · ${block.reason || "Bloqueado"}`;
}

function isUnscheduled(match: TournamentMatch): boolean {
  return !match.scheduled_date || !match.scheduled_time;
}

function isActiveCalendarMatch(match: TournamentMatch): boolean {
  return isActiveCalendarStatus(match.status);
}

function seededOrderValue(matchId: number, seed: number): number {
  const x = Math.sin(matchId * 10007 + seed * 7919) * 10000;
  return x - Math.floor(x);
}

function sortMatches(
  matches: TournamentMatch[],
  mode: CalendarOrderMode,
  randomSeed: number,
): TournamentMatch[] {
  const ordered = [...matches];
  if (mode === "random") {
    ordered.sort(
      (a, b) => seededOrderValue(a.id, randomSeed) - seededOrderValue(b.id, randomSeed) || a.id - b.id,
    );
  } else if (mode === "tournament") {
    ordered.sort(
      (a, b) =>
        String(a.tournament_name ?? "").localeCompare(String(b.tournament_name ?? "")) ||
        String(a.group_name ?? "").localeCompare(String(b.group_name ?? "")) ||
        a.id - b.id,
    );
  } else {
    ordered.sort((a, b) => a.id - b.id);
  }
  // Orden de rondas (revisión 2026-10-03): en cada categoría los partidos
  // previos van antes (cuartos → semis → Final), si no la Final tomaba el
  // primer horario y sus semifinales ya no cabían en ninguno.
  return feedersFirstQueue(ordered);
}

/** «Copa Otoño · Varonil A»: de quién es un aviso en la vista del club. */
function tournamentScopeLabel(match: TournamentMatch): string {
  const tournament = match.tournament_name ?? `Torneo #${match.tournament_id}`;
  return match.division_name ? `${tournament} · ${match.division_name}` : tournament;
}

function tournamentBadge(match: TournamentMatch): string {
  const tournament = match.tournament_name ?? `Torneo #${match.tournament_id}`;
  // La categoría también: «Publicar horarios» es por categoría y día.
  const division = match.division_name ? ` · ${match.division_name}` : "";
  const group = match.group_name ? ` · ${match.group_name}` : "";
  const roundLabel = match.round_display_label ?? match.round_label;
  const round = roundLabel ? ` · ${roundLabel}` : "";
  return `${tournament}${division}${group}${round}`;
}

/** Calendario del torneo abierto en la categoría y el día (no el alias
 * /calendar: ese redirige y pierde ?division y ?fecha). */
function tournamentScheduleHref(match: Pick<TournamentMatch, "tournament_id" | "division_id">, date: string | null): string {
  const params = new URLSearchParams();
  if (match.division_id != null) params.set("division", String(match.division_id));
  if (date) params.set("fecha", date);
  const query = params.toString();
  return `/admin/tournaments/${match.tournament_id}/schedule${query ? `?${query}` : ""}`;
}

interface CalendarRealClub {
  id: number;
  name: string;
  slug: string;
}

interface CalendarClubLayout {
  courts: Array<{ id: number; name: string; sport?: string | null }>;
  window: { opens_at: string; closes_at: string; slot_minutes: number } | null;
}

function addMinutesToTime(time: string, minutes: number): string {
  const [hour, minute] = time.split(":").map(Number);
  const total = hour * 60 + minute + minutes;
  const nextHour = Math.floor(total / 60) % 24;
  const nextMinute = total % 60;
  return `${String(nextHour).padStart(2, "0")}:${String(nextMinute).padStart(2, "0")}`;
}

export default function AdminClubCalendarPage() {
  const { token, user } = useAuth();
  const [venues, setVenues] = useState<string[]>([]);
  const [venueName, setVenueName] = useState("");
  const [realClubs, setRealClubs] = useState<CalendarRealClub[]>([]);
  const [clubLayout, setClubLayout] = useState<CalendarClubLayout | null>(null);
  const [clubReservations, setClubReservations] = useState<ClubReservationInfo[]>([]);
  const [clubMembers, setClubMembers] = useState<ClubMember[]>([]);
  const [isClubStaff, setIsClubStaff] = useState(false);
  const [reserveMemberId, setReserveMemberId] = useState<string>("");
  const [reserveGuests, setReserveGuests] = useState("");
  const [layoutRefresh, setLayoutRefresh] = useState(0);
  // «+ Reservar» del panel «Hoy» llega con ?modo=reservar&club=&fecha=&cancha=&hora=.
  const [searchParams] = useSearchParams();
  const linkDate = searchParams.get("fecha");
  const linkClubId = Number(searchParams.get("club")) || null;
  const reserveHintRef = useRef<{ court: string; time: string } | null>(
    searchParams.get("cancha") && searchParams.get("hora")
      ? { court: searchParams.get("cancha") ?? "", time: searchParams.get("hora") ?? "" }
      : null,
  );
  const [calendarDate, setCalendarDate] = useState(
    linkDate && /^\d{4}-\d{2}-\d{2}$/.test(linkDate) ? linkDate : todayIso(),
  );
  const [calendarStartTime, setCalendarStartTime] = useState("07:00");
  const [calendarEndTime, setCalendarEndTime] = useState("18:00");
  const [calendarInterval, setCalendarInterval] = useState("90");
  const [calendarCourtCount, setCalendarCourtCount] = useState("3");
  const [calendarOrderMode, setCalendarOrderMode] = useState<CalendarOrderMode>("manual");
  // «+ Nueva reserva» del panel del club abre directo en modo reservar.
  const [calendarInteractionMode, setCalendarInteractionMode] = useState<CalendarInteractionMode>(
    searchParams.get("modo") === "reservar" ? "reserve" : "schedule",
  );
  const [blockReason, setBlockReason] = useState("Socios");
  const [blockNotes, setBlockNotes] = useState("");
  const [calendarRandomSeed, setCalendarRandomSeed] = useState(1);
  const [matches, setMatches] = useState<TournamentMatch[]>([]);
  const [blocks, setBlocks] = useState<CourtBlock[]>([]);
  const [selectedMatchId, setSelectedMatchId] = useState<number | null>(null);
  const [loading, setLoading] = useState(false);
  const [calendarBusy, setCalendarBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  // Revisión #418: tocar una hora solo PROPONE el inicio; se guarda (y se
  // avisa a los jugadores) hasta Confirmar en la barra del grid.
  const [proposal, setProposal] = useState<{ court: string; time: string } | null>(null);
  // Explicación del último toque en una celda/tarjeta: va en la barra junto
  // al grid (la alerta de arriba quedaba fuera de la vista en el celular).
  const [gridNotice, setGridNotice] = useState<string | null>(null);
  // Rediseño 2026-10: «Horario y canchas…» abre Desde/Hasta/Bloque/#Canchas;
  // buscador y filtro de torneo de la cola (solo filtran lo que se ve).
  const [showWindowSettings, setShowWindowSettings] = useState(false);
  const [queueQuery, setQueueQuery] = useState("");
  const [queueTournament, setQueueTournament] = useState("");
  // Orden de rondas (revisión 2026-10-03): los que Random / Por torneo dejó
  // sin programar porque el servidor pidió respetar sus partidos previos,
  // con el motivo que dio (aviso fijo, no un toast).
  const [autoRoundOrderWaiting, setAutoRoundOrderWaiting] = useState<string[]>([]);
  const confirmRef = useRef<HTMLButtonElement | null>(null);
  const focusConfirmRef = useRef(false);
  // Club|fecha cuyo horario ya se aplicó a Desde/Hasta/Bloque: recargar el
  // layout tras crear o cancelar una reserva ya no borra lo que el
  // organizador ajustó a mano.
  const appliedWindowKeyRef = useRef<string | null>(null);

  const loadCalendar = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    setError(null);
    try {
      const response = await api.matches.clubCalendar(token, {
        venue_name: venueName || undefined,
        scheduled_date: calendarDate || undefined,
      });
      const nextVenues = response.data.venues;
      setVenues(nextVenues);
      setMatches(response.data.matches);
      setBlocks(response.data.blocks ?? []);
    } catch (err) {
      if (err instanceof ApiError) {
        setError(
          /validation/i.test(err.message)
            ? "Selecciona una sede o revisa la fecha para ver la agenda del club."
            : err.message,
        );
      } else setError("No se pudo cargar la agenda del club.");
    } finally {
      setLoading(false);
    }
  }, [calendarDate, token, venueName]);

  useEffect(() => {
    void loadCalendar();
  }, [loadCalendar]);

  // Real club entities (catalog-backed) for the selector. Empty for plain
  // admins; club staff get theirs; superadmin gets all.
  useEffect(() => {
    if (!token) return;
    api.clubs
      .calendarClubs(token)
      .then((resp) => {
        setRealClubs([...resp.data.clubs].sort((a, b) => a.name.localeCompare(b.name, "es", { sensitivity: "base" })));
        if (!venueName && resp.data.clubs.length > 0) {
          // Club del enlace o el elegido en el panel del club; si no, el primero.
          const wanted = linkClubId ?? readStoredClubId(user?.id);
          const preferred = resp.data.clubs.find((c) => c.id === wanted) ?? resp.data.clubs[0];
          setVenueName(preferred.name);
        }
      })
      .catch(() => setRealClubs([]));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  // Default to the first legacy venue only when there is no real club.
  useEffect(() => {
    if (!venueName && realClubs.length === 0 && venues.length > 0) {
      setVenueName(venues[0]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [venues, realClubs]);

  const selectedRealClub = useMemo(
    () => realClubs.find((club) => club.name === venueName) ?? null,
    [realClubs, venueName],
  );
  // La pregunta es de la sede y el día a la vista: cambiarlos la retira.
  const { askConfirm } = useAbortableConfirm([venueName, calendarDate, selectedRealClub?.id]);

  // Catalog layout: real court names as columns + prefilled hours/slot for
  // the selected date's weekday.
  useEffect(() => {
    if (!token || !selectedRealClub || !calendarDate) {
      setClubLayout(null);
      return;
    }
    let cancelled = false;
    api.clubs
      .calendarLayout(token, selectedRealClub.id, calendarDate)
      .then((resp) => {
        if (cancelled) return;
        setClubLayout({ courts: resp.data.courts, window: resp.data.window });
        setClubReservations(resp.data.reservations ?? []);
        const windowKey = `${selectedRealClub.id}|${calendarDate}`;
        if (resp.data.window && appliedWindowKeyRef.current !== windowKey) {
          setCalendarStartTime(resp.data.window.opens_at);
          setCalendarEndTime(resp.data.window.closes_at);
          setCalendarInterval(String(resp.data.window.slot_minutes));
        }
        appliedWindowKeyRef.current = windowKey;
        setCalendarCourtCount(String(resp.data.courts.length || 0));
      })
      .catch(() => {
        if (!cancelled) {
          setClubLayout(null);
          setClubReservations([]);
        }
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, selectedRealClub?.id, calendarDate, layoutRefresh]);

  // Member roster for the reserve mode — only club staff can list it, so a
  // failure simply hides the mode (e.g. superadmin browsing).
  useEffect(() => {
    if (!token || !selectedRealClub) {
      setClubMembers([]);
      setIsClubStaff(false);
      return;
    }
    api.clubs
      .listMembers(token, selectedRealClub.id)
      .then((resp) => {
        setClubMembers(
          resp.data.members
            .filter((m) => m.status === "active")
            .sort((a, b) =>
              (a.player.display_name ?? a.player.first_name ?? "").localeCompare(
                b.player.display_name ?? b.player.first_name ?? "",
                "es",
                { sensitivity: "base" },
              ),
            ),
        );
        setIsClubStaff(true);
      })
      .catch(() => {
        setClubMembers([]);
        setIsClubStaff(false);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, selectedRealClub?.id]);

  async function handleCreateReservation(slot: string, courtName: string) {
    if (!token || !clubLayout) return;
    const court = clubLayout.courts.find((c) => c.name.trim() === courtName.trim());
    if (!court) {
      flashErr(null, "Esa columna no corresponde a una cancha del catálogo.");
      return;
    }
    setCalendarBusy(true);
    try {
      await api.clubs.createReservation(token, {
        court_id: court.id,
        date: calendarDate,
        start_time: slot,
        end_time: addMinutesToTime(slot, Number(calendarInterval) || 90),
        member_id: reserveMemberId ? Number(reserveMemberId) : null,
        guest_names: reserveGuests.trim() || null,
      }, selectedRealClub?.id ?? null);
      flashOk("Reserva creada.");
      setLayoutRefresh((n) => n + 1);
    } catch (err) {
      flashErr(err, "No se pudo crear la reserva.");
    } finally {
      setCalendarBusy(false);
    }
  }

  async function handleCancelReservation(reservation: ClubReservationInfo) {
    if (!token) return;
    const who = reservation.member_name ?? reservation.guest_names ?? "Sin socio";
    if (!(await askConfirm(`¿Cancelar la reserva de ${who} (${reservation.start_time}–${reservation.end_time})?`, { acceptLabel: "Sí, cancelar la reserva", tone: "danger" }))) {
      return;
    }
    setCalendarBusy(true);
    try {
      await api.clubs.cancelReservation(token, reservation.id, selectedRealClub?.id ?? null);
      flashOk("Reserva cancelada.");
      setLayoutRefresh((n) => n + 1);
    } catch (err) {
      flashErr(err, "No se pudo cancelar la reserva.");
    } finally {
      setCalendarBusy(false);
    }
  }

  // Pista del enlace «+ Reservar»: qué cancha y hora tocar (una sola vez).
  useEffect(() => {
    const hint = reserveHintRef.current;
    if (!hint || !clubLayout) return;
    reserveHintRef.current = null;
    setMessage(`Elige el socio y toca ${hint.court} a las ${hint.time} para reservar.`);
  }, [clubLayout]);

  const calendarMatchDurationMinutes = useMemo(() => {
    const duration = Number(calendarInterval);
    return Number.isFinite(duration) && duration > 0 ? duration : 90;
  }, [calendarInterval]);

  // Cada partido de torneo ocupa la duración de SU categoría (la manda el
  // servidor; Eduardo 2026-10-01: «los bloques de minutos son por
  // categoría»). Sin ella, el bloque de Canchas como antes: es lo que dura al
  // programarlo o moverlo desde aquí.
  const ownMatchDuration = useCallback(
    (match: TournamentMatch): number => validDurationMinutes(match.duration_minutes) ?? calendarMatchDurationMinutes,
    [calendarMatchDurationMinutes],
  );

  const selectedMatch = useMemo(
    () => matches.find((match) => match.id === selectedMatchId) ?? null,
    [matches, selectedMatchId],
  );
  // Lo que ocupará el partido elegido al programarlo o moverlo.
  const selectedMatchDuration = selectedMatch ? ownMatchDuration(selectedMatch) : calendarMatchDurationMinutes;

  // Cada tarjeta del grid. Un partido sin bloque guardado se pinta tan largo
  // como el partido elegido (sin elegido, el bloque de Canchas): es lo que el
  // servidor asume para él al revisar el choque, así una celda "libre" no se
  // rechaza al confirmar (revisión 2026-10-01).
  const clubMatchDuration = useCallback(
    (match: TournamentMatch): number => validDurationMinutes(match.duration_minutes) ?? selectedMatchDuration,
    [selectedMatchDuration],
  );

  // Ventana de INICIOS (Desde–Hasta, inclusive, como antes). El grid tiene
  // filas de 15 min (Eduardo 2026-10-01); el bloque solo define cuánto ocupa
  // cada partido, bloqueo o reserva nueva.
  const calendarWindow = useMemo(() => {
    const startMin = parseTimeMinutes(calendarStartTime);
    const endMin = parseTimeMinutes(calendarEndTime);
    if (startMin === null || endMin === null || endMin < startMin) return null;
    return { startMin, endMin };
  }, [calendarEndTime, calendarStartTime]);

  const calendarCourts = useMemo(() => {
    // Catalog-backed clubs use their real court names as columns; legacy
    // venue strings keep the manual count.
    if (clubLayout && clubLayout.courts.length > 0) {
      return clubLayout.courts.map((court) => court.name);
    }
    return courtsFromCount(calendarCourtCount);
  }, [clubLayout, calendarCourtCount]);

  // Deporte por cancha del catálogo. Solo restringe las OPCIONES que ofrece
  // el programador (match.court es texto libre; lo ya guardado no se valida).
  const courtSportByName = useMemo(() => {
    const map = new Map<string, string>();
    for (const court of clubLayout?.courts ?? []) {
      map.set(court.name.trim(), court.sport ?? "tenis");
    }
    return map;
  }, [clubLayout]);

  const courtAllowsMatch = useCallback(
    (court: string, match: TournamentMatch): boolean => {
      // Una mini cancha (9-A) es del deporte de su cancha (9).
      const courtSport = courtSportByName.get(courtBase(court));
      // Columnas legadas por conteo (sin catálogo) no restringen.
      if (!courtSport) return true;
      return courtSport === (match.tournament_sport ?? "tenis");
    },
    [courtSportByName],
  );

  // El sufijo de deporte en los encabezados solo aporta cuando el club
  // mezcla deportes; los clubes 100% tenis no ven ruido visual nuevo.
  const clubHasMultipleSports = useMemo(
    () => new Set((clubLayout?.courts ?? []).map((c) => c.sport ?? "tenis")).size > 1,
    [clubLayout],
  );

  // Sede EFECTIVA del partido (match_venue_name): uno jugado en la sede
  // adicional de su torneo no ocupa estas canchas. La MISMA regla vale para
  // el grid, los conteos y "Desprogramar todos" (regla #157 del calendario
  // del torneo: nunca operar sobre partidos que no se ven).
  const playsAtThisClub = useCallback(
    (match: TournamentMatch): boolean => {
      const clubKey = venueKey(venueName);
      return !clubKey || !match.match_venue_name || venueKey(match.match_venue_name) === clubKey;
    },
    [venueName],
  );

  // Ocupación por INTERVALO (antes un partido de 17:15 solo aparecía en la
  // fila de 18:00 y la de 17:00 se veía libre): una tarjeta por partido,
  // bloqueo o reserva que abarca sus filas de 15 min.
  const clubGridItems = useMemo(() => {
    const items: ClubGridItem[] = [];
    for (const match of matches) {
      if (!isActiveCalendarMatch(match) || match.scheduled_date !== calendarDate) continue;
      if (!playsAtThisClub(match)) continue;
      const interval = matchGridInterval(match.scheduled_time, clubMatchDuration(match));
      const court = (match.court ?? "").trim();
      if (!interval || !court) continue;
      items.push({
        key: `m-${match.id}`,
        court,
        startMin: interval.startMin,
        endMin: interval.endMin,
        kind: "own",
        matchId: match.id,
        payload: { type: "match", match },
      });
    }
    for (const block of blocks) {
      if (block.scheduled_date !== calendarDate) continue;
      const startMin = parseTimeMinutes(block.start_time);
      const endMin = parseTimeMinutes(block.end_time);
      if (startMin === null || endMin === null || endMin <= startMin) continue;
      items.push({
        key: `b-${block.id}`,
        court: block.court.trim(),
        startMin,
        endMin,
        kind: "block",
        matchId: null,
        payload: { type: "block", block },
      });
    }
    for (const reservation of clubReservations) {
      if (reservation.status !== "active" || !reservation.court_name) continue;
      const startMin = parseTimeMinutes(reservation.start_time);
      const endMin = parseTimeMinutes(reservation.end_time);
      if (startMin === null || endMin === null || endMin <= startMin) continue;
      items.push({
        key: `r-${reservation.id}`,
        court: reservation.court_name.trim(),
        startMin,
        endMin,
        kind: "reservation",
        matchId: null,
        payload: { type: "reservation", reservation },
      });
    }
    return items;
  }, [blocks, calendarDate, clubMatchDuration, clubReservations, matches, playsAtThisClub]);

  // Rango visible: Desde → Hasta + bloque, estirado para que ningún partido
  // de las canchas visibles quede fuera del grid.
  const calendarGridRange = useMemo(() => {
    if (!calendarWindow) return null;
    // Una mini cancha (9-A) se ve en la columna 9: también estira el grid.
    return buildGridRange({
      windowStartMin: calendarWindow.startMin,
      windowEndMin: calendarWindow.endMin,
      durationMinutes: calendarMatchDurationMinutes,
      extendWith: clubGridItems.filter((item) => item.kind === "own" && courtVisibleIn(item.court, calendarCourts)),
    });
  }, [calendarCourts, calendarMatchDurationMinutes, calendarWindow, clubGridItems]);

  const unscheduledMatches = useMemo(
    () =>
      matches.filter(
        (match) => isUnscheduled(match) && isBulkProgrammableCalendarStatus(match.status),
      ),
    [matches],
  );

  const orderedUnscheduledMatches = useMemo(
    () => sortMatches(unscheduledMatches, calendarOrderMode, calendarRandomSeed),
    [calendarOrderMode, calendarRandomSeed, unscheduledMatches],
  );

  const scheduledMatches = useMemo(
    () =>
      matches.filter(
        (match) =>
          match.scheduled_date === calendarDate &&
          Boolean(match.scheduled_time || match.court) &&
          isActiveCalendarMatch(match) &&
          playsAtThisClub(match),
      ),
    [calendarDate, matches, playsAtThisClub],
  );

  // Publicar horarios (Eduardo 2026-10-01): los horarios de este día que los
  // jugadores aún no ven, por torneo y categoría. Se publican desde el
  // calendario de cada torneo (ahí está el botón «Publicar horarios»).
  const unpublishedToday = useMemo(() => {
    const rows = new Map<string, { match: TournamentMatch; count: number }>();
    for (const match of scheduledMatches) {
      if (!isScheduleUnpublished(match)) continue;
      const key = `${match.tournament_id}|${match.division_id ?? ""}`;
      const row = rows.get(key);
      if (row) row.count += 1;
      else rows.set(key, { match, count: 1 });
    }
    return [...rows.values()];
  }, [scheduledMatches]);
  const unpublishedTodayCount = unpublishedToday.reduce((sum, row) => sum + row.count, 0);
  const unpublishedTodayTournaments = new Set(unpublishedToday.map((row) => row.match.tournament_id)).size;

  function flashOk(text: string) {
    setMessage(text);
    setError(null);
  }

  function flashErr(err: unknown, fallback: string) {
    if (err instanceof ApiError) setError(err.message);
    else setError(fallback);
    setMessage(null);
  }

  function handleSelectMatch(match: TournamentMatch) {
    setSelectedMatchId(match.id);
    setError(null);
    setMessage(`Seleccionado para mover: ${matchShortLabel(match)}.`);
  }

  function handleQueueSelect(match: TournamentMatch) {
    setSelectedMatchId(match.id);
    setError(null);
  }

  // Cambiar de partido, modo, club o fecha descarta la hora propuesta.
  useEffect(() => {
    setProposal(null);
    setGridNotice(null);
  }, [selectedMatchId, calendarInteractionMode, calendarDate, venueName]);
  useEffect(() => {
    setAutoRoundOrderWaiting([]);
  }, [calendarDate, venueName]);
  // Día y club a la vista AHORA (no los que el clic dejó capturados): Random /
  // Por torneo manda un pedido por partido y tarda segundos, con Fecha y Club
  // activos. Si el admin cambia mientras tanto, los motivos del día que se
  // programó no se pintan bajo el otro (mismo arreglo que el calendario del
  // torneo).
  const roundOrderScopeRef = useRef({ date: calendarDate, venue: venueName });
  useLayoutEffect(() => {
    roundOrderScopeRef.current = { date: calendarDate, venue: venueName };
  }, [calendarDate, venueName]);

  useEffect(() => {
    if (!proposal || !focusConfirmRef.current) return;
    focusConfirmRef.current = false;
    confirmRef.current?.focus({ preventScroll: true });
  }, [proposal]);

  /** Lo que se manda al mover/programar desde Canchas. La sede va explícita
   * (el backend guarda la principal como NULL): un partido con una sede
   * adicional vieja ya no queda "programado" fuera de este grid. El choque
   * de JUGADOR sigue por hora exacta: el bloque de Canchas es el de las
   * reservas del club, no la duración de cada torneo (revisión #418). */
  function clubScheduleExtras(match: TournamentMatch) {
    return {
      ...(venueName ? { venue_name: venueName } : {}),
      // La duración del partido (la de su categoría o, sin ella, el bloque).
      duration_minutes: durationForServer(ownMatchDuration(match)),
      check_players_by_overlap: false,
    };
  }

  /** Por qué no cabe algo nuevo ahí: nombra lo que ya ocupa y su rango. */
  function clubConflictText(item: ClubGridItem, mode: CalendarInteractionMode): string {
    const range = intervalLabel(item.startMin, item.endMin);
    const payload = item.payload;
    if (payload.type === "block") return `Cancha bloqueada ${range} · ${payload.block.reason || "Bloqueado"}.`;
    if (payload.type === "reservation") {
      return `Ya hay una reserva de ${payload.reservation.member_name ?? "club"} (${range}).`;
    }
    const what = `${matchShortLabel(payload.match)} (${range}) · ${payload.match.tournament_name ?? `Torneo #${payload.match.tournament_id}`}`;
    return mode === "schedule" ? `No cabe aquí: choca con ${what}.` : `Ahí hay un partido programado: ${what}.`;
  }

  async function handleSchedule(match: TournamentMatch, scheduledTime: string, court: string) {
    if (!token || !calendarDate) return;
    if (!courtAllowsMatch(court, match)) {
      setError(
        `${courtDisplayName(court)} es de otro deporte; este partido es de un torneo de ${sportLabel(match.tournament_sport)}.`,
      );
      setMessage(null);
      return;
    }
    const startMin = parseTimeMinutes(scheduledTime);
    if (startMin === null) return;
    // Intervalo [inicio, inicio+bloque) contra partidos, bloqueos y reservas
    // de la cancha; el partido que se mueve no se estorba a sí mismo.
    const matchMinutes = ownMatchDuration(match);
    const check = startAvailability(court, startMin, matchMinutes, clubItemsForPlacing(clubGridItems, matchMinutes), {
      excludeMatchId: match.id,
    });
    if (!check.ok && check.reason !== "window") {
      const payload = check.item.payload;
      if (payload.type === "block") {
        setError(`Ese horario está bloqueado: ${blockLabel(payload.block)}.`);
      } else if (payload.type === "reservation") {
        setError(
          `Ese horario tiene una reserva de ${payload.reservation.member_name ?? "club"} (${payload.reservation.start_time}–${payload.reservation.end_time}). Cancélala primero si quieres programar ahí.`,
        );
      } else {
        setError(
          `Ese horario y cancha ya tienen partido en este club: ${matchShortLabel(payload.match)} (${intervalLabel(check.item.startMin, check.item.endMin)}).`,
        );
      }
      setMessage(null);
      return;
    }
    setCalendarBusy(true);
    try {
      await api.matches.update(token, match.tournament_id, match.id, {
        scheduled_date: calendarDate,
        scheduled_time: scheduledTime,
        court,
        status: match.status,
        round_label: match.round_label,
        notes: match.notes,
        // El servidor revisa la cancha por intervalo con este bloque.
        ...clubScheduleExtras(match),
      });
      setSelectedMatchId(null);
      flashOk(`Programado: ${matchShortLabel(match)} · ${courtDisplayName(court)} ${scheduledTime}`);
      await loadCalendar();
    } catch (err) {
      flashErr(err, "No se pudo programar el partido en ese horario.");
    } finally {
      setCalendarBusy(false);
    }
  }

  async function handleAutoCalendarProgram(
    mode: CalendarOrderMode = calendarOrderMode,
    randomSeed: number = calendarRandomSeed,
  ) {
    if (!token || !calendarDate) return;
    if (!calendarWindow || calendarCourts.length === 0) {
      setError("Define horarios y canchas válidos antes de ordenar.");
      setMessage(null);
      return;
    }
    const queue = sortMatches(unscheduledMatches, mode, randomSeed);
    // Inicios en pasos del bloque desde Desde (como el orden automático del
    // torneo): con filas de 15 min, probar cada fila amontonaba partidos
    // cada 15 minutos.
    const starts = steppedStarts(calendarWindow.startMin, calendarWindow.endMin, calendarMatchDurationMinutes);
    const occupied: ClubGridItem[] = [...clubGridItems];
    let programmedCount = 0;
    let leftOutCount = 0;
    const roundOrderWaiting: string[] = [];

    setCalendarBusy(true);
    // Los motivos de la corrida anterior ya no aplican.
    setAutoRoundOrderWaiting([]);
    const programmedDate = calendarDate;
    const programmedVenue = venueName;
    try {
      for (const match of queue) {
        let programmed = false;
        // El último «respeta sus partidos previos» que dio el servidor: si
        // no cabe en ningún horario, ese es el motivo (no «sin espacio»).
        let roundOrderReason: string | null = null;
        // Lo que dura ESTE partido; los de categorías sin bloque guardado se
        // revisan con esa misma duración (como el servidor).
        const matchMinutes = ownMatchDuration(match);
        for (const startMin of starts) {
          const time = timeFromMinutes(startMin);
          for (const court of calendarCourts) {
            if (!courtAllowsMatch(court, match)) continue;
            if (!startAvailability(court, startMin, matchMinutes, clubItemsForPlacing(occupied, matchMinutes)).ok) continue;
            try {
              await api.matches.update(token, match.tournament_id, match.id, {
                scheduled_date: calendarDate,
                scheduled_time: time,
                court,
                status: match.status,
                round_label: match.round_label,
                notes: match.notes,
                ...clubScheduleExtras(match),
              });
              occupied.push({
                key: `auto-${match.id}`,
                court: court.trim(),
                startMin,
                endMin: startMin + matchMinutes,
                kind: "own",
                matchId: match.id,
                payload: { type: "match", match },
              });
              programmedCount += 1;
              programmed = true;
              break;
            } catch (err) {
              if (err instanceof ApiError && err.status === 409) {
                if (err.code === "round_order_conflict") roundOrderReason = err.message;
                continue;
              }
              throw err;
            }
          }
          if (programmed) break;
        }
        if (programmed) continue;
        if (roundOrderReason) roundOrderWaiting.push(`${tournamentScopeLabel(match)}: ${roundOrderReason}`);
        else leftOutCount += 1;
      }

      const scope = roundOrderScopeRef.current;
      const sameScope = scope.date === programmedDate && scope.venue === programmedVenue;
      if (sameScope) setAutoRoundOrderWaiting(roundOrderWaiting);
      // El toast sí sale (es de la acción), pero solo manda «abajo» si la
      // lista está a la vista.
      const whereReason = sameScope ? " (abajo el motivo)" : "";
      const waitingText =
        roundOrderWaiting.length === 0
          ? ""
          : roundOrderWaiting.length === 1
            ? ` 1 quedó sin programar por el orden de las rondas${whereReason}.`
            : ` ${roundOrderWaiting.length} quedaron sin programar por el orden de las rondas${whereReason}.`;
      if (programmedCount === 0) {
        flashOk(`No hay horarios/canchas libres para programar.${waitingText}`);
      } else {
        flashOk(
          `Programados ${programmedCount} partido(s).${
            leftOutCount > 0 ? ` Quedaron ${leftOutCount} sin espacio, con cruce de horario o sin cancha de su deporte.` : ""
          }${waitingText}`,
        );
      }
      setSelectedMatchId(null);
      await loadCalendar();
    } catch (err) {
      flashErr(err, "No se pudieron programar todos los partidos.");
      await loadCalendar();
    } finally {
      setCalendarBusy(false);
    }
  }

  function handleOrderModeClick(mode: CalendarOrderMode) {
    if (mode === "manual") {
      setCalendarOrderMode("manual");
      return;
    }
    const nextSeed = mode === "random" ? calendarRandomSeed + 1 : calendarRandomSeed;
    setCalendarOrderMode(mode);
    if (mode === "random") setCalendarRandomSeed(nextSeed);
    void handleAutoCalendarProgram(mode, nextSeed);
  }

  async function handleUnscheduleDay() {
    if (!token || scheduledMatches.length === 0) return;
    // Publicar horarios: los ya publicados avisan «sin horario» a sus
    // jugadores (un aviso por jugador y torneo); los sin publicar, callados.
    const publishedCount = scheduledMatches.filter((match) => isSchedulePublished(match)).length;
    const question =
      `¿Desprogramar ${scheduledMatches.length} partidos de ${calendarDate}?` +
      (publishedCount > 0
        ? `\n${publishedCount === 1 ? "1 ya estaba publicado: sus jugadores recibirán" : `${publishedCount} ya estaban publicados: sus jugadores recibirán`} un aviso de que su partido quedó sin horario.`
        : "\nNinguno estaba publicado: no se avisa a nadie.");
    if (!(await askConfirm(question, { acceptLabel: "Sí, desprogramar el día", tone: "danger" }))) return;
    setCalendarBusy(true);
    try {
      // Un pedido por torneo (no uno por partido): así cada jugador recibe UN
      // aviso con todos sus partidos que se quedaron sin horario.
      const byTournament = new Map<number, number[]>();
      for (const match of scheduledMatches) {
        const ids = byTournament.get(match.tournament_id) ?? [];
        ids.push(match.id);
        byTournament.set(match.tournament_id, ids);
      }
      let updated = 0;
      for (const [tournamentId, matchIds] of byTournament) {
        const response = await api.matches.bulkUnschedule(token, tournamentId, { match_ids: matchIds });
        updated += response.data.summary.updated;
      }
      flashOk(`Desprogramados ${updated} partido(s).`);
      setSelectedMatchId(null);
      await loadCalendar();
    } catch (err) {
      flashErr(err, "No se pudieron desprogramar todos los partidos.");
      await loadCalendar();
    } finally {
      setCalendarBusy(false);
    }
  }

  async function handleCreateBlock(slot: string, court: string) {
    if (!token || !venueName || !calendarDate) {
      setError("Selecciona club y fecha antes de bloquear.");
      setMessage(null);
      return;
    }
    const startMinutes = parseTimeMinutes(slot);
    if (startMinutes === null) return;
    const check = startAvailability(court, startMinutes, calendarMatchDurationMinutes, clubGridItems);
    if (!check.ok && check.reason !== "window") {
      setError(clubConflictText(check.item, "block"));
      setMessage(null);
      return;
    }
    const interval = Number(calendarInterval) || 90;
    const endTime = timeFromMinutes(Math.min(startMinutes + interval, 23 * 60 + 59));
    setCalendarBusy(true);
    try {
      await api.matches.createCourtBlock(token, {
        venue_name: venueName,
        scheduled_date: calendarDate,
        start_time: slot,
        end_time: endTime,
        court,
        reason: blockReason || "Bloqueado",
        notes: blockNotes || null,
        match_duration_minutes: durationForServer(calendarMatchDurationMinutes),
      });
      flashOk(`Bloqueado: ${courtDisplayName(court)} ${slot}–${endTime}.`);
      await loadCalendar();
    } catch (err) {
      flashErr(err, "No se pudo bloquear ese horario.");
      await loadCalendar();
    } finally {
      setCalendarBusy(false);
    }
  }

  async function handleDeleteBlock(block: CourtBlock) {
    if (!token) return;
    if (!(await askConfirm(`¿Liberar ${courtDisplayName(block.court)} ${block.start_time}–${block.end_time}?`, { acceptLabel: "Sí, liberar la cancha" }))) return;
    setCalendarBusy(true);
    try {
      await api.matches.deleteCourtBlock(token, block.id);
      flashOk(`Liberado: ${courtDisplayName(block.court)} ${block.start_time}–${block.end_time}.`);
      await loadCalendar();
    } catch (err) {
      flashErr(err, "No se pudo liberar el bloqueo.");
      await loadCalendar();
    } finally {
      setCalendarBusy(false);
    }
  }

  const hasInvalidSlots = !calendarGridRange || calendarCourts.length === 0;

  /** Cada celda de 15 min es un posible INICIO (de partido, bloqueo o
   * reserva): se puede tocar solo si [hora, hora+bloque) cabe libre. */
  function clubCellState(court: string, slotMin: number): CourtTimeGridCell {
    const time = timeFromMinutes(slotMin);
    if (!calendarWindow || slotMin < calendarWindow.startMin || slotMin > calendarWindow.endMin) {
      return {
        enabled: false,
        tone: "outside",
        title: `Fuera del horario: los inicios van de ${friendlyTime12h(calendarStartTime)} a ${friendlyTime12h(calendarEndTime)}.`,
      };
    }
    if (calendarInteractionMode === "block" || calendarInteractionMode === "reserve") {
      const range = intervalLabel(slotMin, slotMin + calendarMatchDurationMinutes);
      const check = startAvailability(court, slotMin, calendarMatchDurationMinutes, clubGridItems);
      if (!check.ok && check.reason !== "window") {
        return { enabled: false, tone: "nofit", title: clubConflictText(check.item, calendarInteractionMode) };
      }
      return {
        enabled: true,
        tone: "free",
        title: `${calendarInteractionMode === "block" ? "Bloquear" : "Reservar"} ${courtDisplayName(court)} ${range}`,
      };
    }
    // Celda libre deshabilitada por deporte: explica el motivo.
    if (selectedMatch && !courtAllowsMatch(court, selectedMatch)) {
      return {
        enabled: false,
        tone: "nofit",
        title: `Cancha de ${sportLabel(courtSportByName.get(court.trim()))}; el partido es de ${sportLabel(selectedMatch.tournament_sport)}.`,
      };
    }
    const range = intervalLabel(slotMin, slotMin + selectedMatchDuration);
    // Las tarjetas sin bloque guardado ya se pintan con la duración del
    // partido elegido (clubMatchDuration): misma revisión que el servidor.
    const check = startAvailability(court, slotMin, selectedMatchDuration, clubGridItems, {
      excludeMatchId: selectedMatch?.id ?? null,
    });
    if (!check.ok && check.reason !== "window") {
      return { enabled: false, tone: "nofit", title: clubConflictText(check.item, "schedule") };
    }
    if (!selectedMatch) {
      return {
        enabled: false,
        tone: "idle",
        title: `${courtDisplayName(court)} ${time} libre. Selecciona un partido primero.`,
      };
    }
    return {
      enabled: true,
      tone: "free",
      title: `${selectedMatch.scheduled_date ? "Mover" : "Programar"} ${matchShortLabel(selectedMatch)} a ${courtDisplayName(court)} ${range}`,
    };
  }

  /** El partido ya está en esa cancha y hora de esta fecha. */
  function isCurrentClubSlot(match: TournamentMatch, court: string, time: string): boolean {
    return (
      match.scheduled_date === calendarDate &&
      (match.scheduled_time ?? "").trim() === time &&
      (match.court ?? "").trim() === court &&
      playsAtThisClub(match)
    );
  }

  /** Programar: el toque solo PROPONE el inicio (hay que Confirmar). Bloquear
   * y Reservar siguen de un toque. */
  function handleClubCellClick(court: string, time: string, info?: { viaKeyboard: boolean }) {
    setGridNotice(null);
    if (calendarInteractionMode === "block") {
      void handleCreateBlock(time, court);
      return;
    }
    if (calendarInteractionMode === "reserve") {
      void handleCreateReservation(time, court);
      return;
    }
    if (!selectedMatch) return;
    if (isCurrentClubSlot(selectedMatch, court, time)) {
      setProposal(null);
      setGridNotice(`Ese ya es su horario (${courtDisplayName(court)} ${time}). Toca otra hora para moverlo.`);
      return;
    }
    focusConfirmRef.current = Boolean(info?.viaKeyboard);
    setProposal({ court, time });
  }

  function proposalCanShift(delta: number): boolean {
    if (!proposal || !selectedMatch) return false;
    const start = parseTimeMinutes(proposal.time);
    if (start === null) return false;
    const next = start + delta;
    if (next < 0 || next >= 24 * 60) return false;
    if (isCurrentClubSlot(selectedMatch, proposal.court, timeFromMinutes(next))) return false;
    return clubCellState(proposal.court, next).enabled;
  }

  function handleNudge(delta: number) {
    if (!proposal || !proposalCanShift(delta)) return;
    const start = parseTimeMinutes(proposal.time);
    if (start === null) return;
    setProposal({ court: proposal.court, time: timeFromMinutes(start + delta) });
  }

  function handleConfirmProposal() {
    if (!selectedMatch || !proposal) return;
    void handleSchedule(selectedMatch, proposal.time, proposal.court);
  }

  function renderClubGridItem(item: ClubGridItem, info: CourtTimeGridItemInfo) {
    const compact = info.span <= 2;
    // Mini canchas: un partido en 9-A/9-B es una tarjeta de media columna.
    const narrow = info.narrow;
    const range = intervalLabel(item.startMin, item.endMin);
    const stateClass = `${compact ? " is-compact" : ""}${info.conflict ? " is-conflict" : ""}${
      narrow ? " schedule-timegrid__card--half" : ""
    }`;
    const halfTag = info.half && info.lanes > 1 ? (
      <span className="schedule-timegrid__half-tag" aria-hidden="true">
        {info.half}
      </span>
    ) : null;
    const halfTitle = info.half ? `${courtDisplayName(item.court)} · ` : "";
    const payload = item.payload;
    if (payload.type === "reservation") {
      const reservation = payload.reservation;
      const who = reservation.member_name ?? "Club";
      return (
        <button
          type="button"
          className={`schedule-timegrid__card schedule-timegrid__card--reservation${stateClass}`}
          disabled={calendarBusy || !isClubStaff}
          title={isClubStaff ? `Toca para cancelar la reserva de ${who} (${range})` : `Reserva de ${who} (${range})`}
          onClick={() => void handleCancelReservation(reservation)}
        >
          <strong>Reserva · {who}</strong>
          <small>
            {range}
            {reservation.guest_names ? ` · ${reservation.guest_names}` : ""}
          </small>
        </button>
      );
    }
    if (payload.type === "block") {
      const block = payload.block;
      const reason = block.reason || "Bloqueado";
      return (
        <button
          type="button"
          className={`schedule-timegrid__card schedule-timegrid__card--block${stateClass}`}
          disabled={calendarBusy}
          title={
            calendarInteractionMode === "block"
              ? `Toca para liberar ${courtDisplayName(block.court)} ${range} · ${reason}`
              : `${halfTitle}Cancha bloqueada ${range} · ${reason}. Para liberarla usa el modo Bloquear.`
          }
          onClick={() => {
            if (calendarInteractionMode === "block") {
              void handleDeleteBlock(block);
              return;
            }
            setGridNotice(`Cancha bloqueada ${range} · ${reason} — para liberarla usa el modo Bloquear.`);
          }}
        >
          <strong>⛔ {reason}</strong>
          <small>
            {halfTag}
            {range}
            {block.notes ? ` · ${block.notes}` : ""}
          </small>
        </button>
      );
    }
    const match = payload.match;
    const label = matchShortLabel(match);
    const cardLabel = narrow ? matchShortLabel(match, { short: true }) : label;
    const tournamentName = match.tournament_name ?? `Torneo #${match.tournament_id}`;
    const isSelected = selectedMatchId === match.id;
    // Publicar horarios: marca escrita, igual que en el calendario del torneo.
    const unpublishedCard = isScheduleUnpublished(match);
    const draftTitle = unpublishedCard ? "Sin publicar: los jugadores aún no ven este horario. " : "";
    // Tarjeta corta o de media cancha (angosta): el punto, no la etiqueta
    // «SIN PUBLICAR», que no cabe y le quita renglones a los nombres.
    const draftMark = unpublishedCard ? (
      compact || narrow ? (
        <span className="schedule-timegrid__draft-dot" role="img" aria-label="Sin publicar" />
      ) : (
        <span className="schedule-timegrid__draft-tag">Sin publicar</span>
      )
    ) : null;
    return (
      <button
        type="button"
        className={`schedule-timegrid__card${stateClass}${isSelected ? " is-selected is-moving" : ""}${unpublishedCard ? " is-unpublished" : ""}`}
        disabled={calendarBusy}
        title={
          calendarInteractionMode === "schedule"
            ? `${halfTitle}${draftTitle}Toca para seleccionar y mover: ${label} (${range}) · ${tournamentName}`
            : `${halfTitle}${draftTitle}Ahí hay un partido programado: ${label} (${range}) · ${tournamentName}`
        }
        onClick={() => {
          if (calendarInteractionMode !== "schedule") {
            setGridNotice(`Ahí hay un partido programado (${label}, ${range}) — cambia a Programar para moverlo.`);
            return;
          }
          handleSelectMatch(match);
        }}
      >
        {compact || narrow ? (
          <>
            {draftMark}
            <small>
              {halfTag}
              {timeFromMinutes(item.startMin)}
            </small>
            <strong>{cardLabel}</strong>
          </>
        ) : (
          <>
            {draftMark}
            <strong>{label}</strong>
            <small>
              {range} · {tournamentName}
            </small>
          </>
        )}
      </button>
    );
  }

  const firstMatchStart = clubGridItems
    .filter((item) => item.kind === "own" && courtVisibleIn(item.court, calendarCourts))
    .reduce<number | null>((min, item) => (min === null ? item.startMin : Math.min(min, item.startMin)), null);
  const clubScrollToMin = calendarWindow
    ? firstMatchStart === null ? calendarWindow.startMin : Math.min(calendarWindow.startMin, firstMatchStart)
    : null;
  const proposalStart =
    calendarInteractionMode === "schedule" && selectedMatch && proposal ? parseTimeMinutes(proposal.time) : null;
  const proposalView =
    proposal && proposalStart !== null
      ? {
          court: proposal.court,
          courtLabel: courtDisplayName(proposal.court),
          startMin: proposalStart,
          endMin: proposalStart + selectedMatchDuration,
        }
      : null;

  const timeLabel = (value: string) =>
    calendarTimeOptionsFor(value).find((option) => option.value === value)?.label ?? value;
  // Cola: buscador y filtro por torneo SOLO de lo que se ve (el orden
  // automático sigue usando la cola completa).
  const queueTournaments = Array.from(
    new Map(
      orderedUnscheduledMatches.map((match) => [
        String(match.tournament_id),
        match.tournament_name ?? `Torneo #${match.tournament_id}`,
      ]),
    ),
  ).sort((a, b) => a[1].localeCompare(b[1], "es", { sensitivity: "base" }));
  const queueNeedle = normalizeQueueText(queueQuery.trim());
  const visibleQueue = orderedUnscheduledMatches.filter((match) => {
    if (queueTournament && String(match.tournament_id) !== queueTournament) return false;
    if (!queueNeedle) return true;
    return normalizeQueueText(`${matchShortLabel(match)} ${tournamentBadge(match)}`).includes(queueNeedle);
  });

  return (
    <section className="page-stack adm-courts">
      <header className="adm-players__head">
        <div>
          <span className="adm-players__eyebrow">Vista del club</span>
          <h1 className="adm-players__title">Programador de canchas</h1>
          <p className="adm-courts__lead">
            Programa partidos de todos los torneos del club en una sola vista, evitando cruces de cancha y jugador.
          </p>
        </div>
        <div className="adm-players__head-actions adm-courts__head-actions">
          <select className="t123-select adm-courts__club" aria-label="Club" value={venueName} onChange={(e) => setVenueName(e.target.value)}>
            {realClubs.length === 0 && venues.length === 0 ? (
              <option value="">Sin clubes capturados</option>
            ) : null}
            {realClubs.map((club) => (
              <option key={`club-${club.id}`} value={club.name}>
                🏟 {club.name}
              </option>
            ))}
            {venues
              .filter((venue) => !realClubs.some((club) => club.name === venue))
              .map((venue) => (
                <option key={venue} value={venue}>
                  {venue}
                </option>
              ))}
          </select>
          <Link className="adm-players__btn adm-players__btn--secondary" to="/admin/tournaments">
            Ver torneos
          </Link>
        </div>
      </header>

      {message && <div className="alert alert--success">{message}</div>}
      {error && <div className="alert alert--danger">{error}</div>}

      <section className="adm-courts__day" aria-label="Resumen del día">
        <div className="adm-courts__day-nav">
          <button type="button" className="adm-courts__day-arrow" aria-label="Día anterior" onClick={() => setCalendarDate(shiftIsoDate(calendarDate, -1))} disabled={!calendarDate}>
            ‹
          </button>
          {/* Fecha resaltada y con el día de la semana, igual que en el calendario del torneo. */}
          <CalendarDateField value={calendarDate} onChange={setCalendarDate} today={todayIso()} />
          <button type="button" className="adm-courts__day-arrow" aria-label="Día siguiente" onClick={() => setCalendarDate(shiftIsoDate(calendarDate, 1))} disabled={!calendarDate}>
            ›
          </button>
        </div>
        <div className="adm-courts__counters">
          <span className="adm-courts__counter adm-courts__counter--lime">{orderedUnscheduledMatches.length} por programar</span>
          <span className="adm-courts__counter">{scheduledMatches.length} programados</span>
          <span className="adm-courts__counter">{blocks.length} bloqueos</span>
          <span className="adm-courts__counter">
            {calendarCourts.length || "—"} canchas · {timeLabel(calendarStartTime)} a {timeLabel(calendarEndTime)}
          </span>
        </div>
      </section>

      <section className="adm-courts__toolbar" aria-label="Herramientas">
        <div className="adm-courts__group">
          <span className="adm-courts__group-label">Qué haces al tocar una cancha</span>
          <div className="adm-courts__segmented">
            <button type="button" className={calendarInteractionMode === "schedule" ? "is-active" : ""} aria-pressed={calendarInteractionMode === "schedule"} onClick={() => setCalendarInteractionMode("schedule")} disabled={calendarBusy}>Programar partido</button>
            <button type="button" className={calendarInteractionMode === "block" ? "is-active" : ""} aria-pressed={calendarInteractionMode === "block"} onClick={() => { setCalendarInteractionMode("block"); setSelectedMatchId(null); }} disabled={calendarBusy}>Bloquear horario</button>
            {isClubStaff && clubLayout ? (
              <button type="button" className={calendarInteractionMode === "reserve" ? "is-active" : ""} aria-pressed={calendarInteractionMode === "reserve"} onClick={() => { setCalendarInteractionMode("reserve"); setSelectedMatchId(null); }} disabled={calendarBusy}>Reservar</button>
            ) : null}
          </div>
        </div>
        <div className="adm-courts__group">
          <span className="adm-courts__group-label">Programar</span>
          <div className="adm-courts__pills">
            <button type="button" className={calendarOrderMode === "manual" ? "is-active" : ""} aria-pressed={calendarOrderMode === "manual"} onClick={() => handleOrderModeClick("manual")} disabled={calendarBusy}>A mano</button>
            <button type="button" className={calendarOrderMode === "random" ? "is-active" : ""} aria-pressed={calendarOrderMode === "random"} onClick={() => handleOrderModeClick("random")} disabled={calendarBusy || orderedUnscheduledMatches.length === 0}>Automático al azar</button>
            <button type="button" className={calendarOrderMode === "tournament" ? "is-active" : ""} aria-pressed={calendarOrderMode === "tournament"} onClick={() => handleOrderModeClick("tournament")} disabled={calendarBusy || orderedUnscheduledMatches.length === 0}>Automático por torneo</button>
          </div>
        </div>
        <div className="adm-courts__tool-actions">
          <button
            type="button"
            className={`adm-courts__tool-btn${showWindowSettings ? " is-active" : ""}`}
            aria-expanded={showWindowSettings}
            aria-controls="adm-courts-window"
            onClick={() => setShowWindowSettings((open) => !open)}
          >
            Horario y canchas…
          </button>
          <button type="button" className="adm-courts__tool-btn adm-courts__tool-btn--danger" onClick={() => void handleUnscheduleDay()} disabled={calendarBusy || scheduledMatches.length === 0}>Desprogramar el día</button>
          <button type="button" className="adm-courts__tool-btn" onClick={() => void loadCalendar()} disabled={loading}>{loading ? "Cargando…" : "Actualizar"}</button>
        </div>

        <div className="adm-courts__window" id="adm-courts-window" hidden={!showWindowSettings}>
          {/* Inicios cada 15 min en 12h, igual que el calendario del torneo. */}
          <label className="schedule-toolbar__field">
            <span className="schedule-toolbar__label">Desde</span>
            <select className="t123-select" value={calendarStartTime} onChange={(e) => setCalendarStartTime(e.target.value)} aria-label="Hora del primer inicio">
              {calendarTimeOptionsFor(calendarStartTime).map((option) => (
                <option key={option.value} value={option.value}>{option.label}</option>
              ))}
            </select>
          </label>
          <label className="schedule-toolbar__field">
            <span className="schedule-toolbar__label">Hasta</span>
            <select className="t123-select" value={calendarEndTime} onChange={(e) => setCalendarEndTime(e.target.value)} aria-label="Hora del último inicio" title="Último horario en que puede EMPEZAR un partido, bloqueo o reserva.">
              {calendarTimeOptionsFor(calendarEndTime).map((option) => (
                <option key={option.value} value={option.value}>{option.label}</option>
              ))}
            </select>
          </label>
          <label className="schedule-toolbar__field"><span className="schedule-toolbar__label">Bloque min</span><input type="number" min={15} max={240} step={15} value={calendarInterval} onChange={(e) => setCalendarInterval(e.target.value)} /></label>
          <label className="schedule-toolbar__field"><span className="schedule-toolbar__label">#Canchas</span><input type="number" min={1} max={24} value={calendarCourtCount} onChange={(e) => setCalendarCourtCount(e.target.value)} disabled={Boolean(clubLayout)} title={clubLayout ? "Las canchas salen del catálogo de Mi Club" : undefined} /></label>
        </div>

        {calendarInteractionMode === "block" ? (
          <div className="adm-courts__mode-fields">
            <label className="schedule-toolbar__field"><span className="schedule-toolbar__label">Motivo</span><input value={blockReason} onChange={(e) => setBlockReason(e.target.value)} placeholder="Socios" /></label>
            <label className="schedule-toolbar__field schedule-toolbar__field--grow"><span className="schedule-toolbar__label">Notas</span><input value={blockNotes} onChange={(e) => setBlockNotes(e.target.value)} placeholder="Opcional" /></label>
          </div>
        ) : null}
        {calendarInteractionMode === "reserve" ? (
          <div className="adm-courts__mode-fields">
            <label className="schedule-toolbar__field schedule-toolbar__field--grow">
              <span className="schedule-toolbar__label">Socio</span>
              <select className="t123-select" value={reserveMemberId} onChange={(e) => setReserveMemberId(e.target.value)}>
                <option value="">Sin socio (reserva del club)</option>
                {clubMembers.map((member) => (
                  <option key={member.id} value={String(member.id)}>
                    {member.player.display_name ?? member.player.first_name}
                  </option>
                ))}
              </select>
            </label>
            <label className="schedule-toolbar__field schedule-toolbar__field--grow"><span className="schedule-toolbar__label">Invitados</span><input value={reserveGuests} onChange={(e) => setReserveGuests(e.target.value)} placeholder="Opcional" /></label>
          </div>
        ) : null}
        <small className="adm-courts__note">
          {calendarInteractionMode === "reserve"
            ? "Elige socio (opcional) y toca una celda libre para reservarla. Toca una reserva para cancelarla."
            : calendarInteractionMode === "block"
              ? "En Bloquear, toca una celda libre para bloquearla (socios, clase o mantenimiento)."
              : "Los dos automáticos programan solos la cola del día."}
        </small>
        {autoRoundOrderWaiting.length > 0 ? (
          <div className="schedule-order__waiting" role="note" data-no-toast data-club-order-waiting>
            <p className="schedule-order__waiting-title">
              <strong>Quedaron sin programar por el orden de las rondas:</strong>
            </p>
            <ul>
              {autoRoundOrderWaiting.map((reason, index) => (
                <li key={`${index}-${reason}`}>{reason}</li>
              ))}
            </ul>
          </div>
        ) : null}
      </section>

      {unpublishedTodayCount > 0 ? (
        <section
          className="alert alert--warning schedule-publish-notice"
          data-no-toast
          role="status"
          aria-live="polite"
          data-club-unpublished-notice
        >
          <p className="schedule-publish-notice__title">
            <strong>
              Hay {horariosText(unpublishedTodayCount)} sin publicar este día en{" "}
              {unpublishedTodayTournaments === 1 ? "1 torneo" : `${unpublishedTodayTournaments} torneos`}:
              los jugadores aún no {unpublishedTodayCount === 1 ? "lo ven" : "los ven"}.
            </strong>
          </p>
          <p>Se publican desde el calendario de cada torneo, por categoría y día.</p>
          <ul className="schedule-publish-notice__rows">
            {unpublishedToday.map(({ match, count }) => (
              <li key={`${match.tournament_id}-${match.division_id ?? ""}`}>
                <span>
                  {match.tournament_name ?? `Torneo #${match.tournament_id}`}
                  {match.division_name ? ` · ${match.division_name}` : ""}: {count} sin publicar
                </span>{" "}
                <Link
                  className="btn btn--ghost btn--compact"
                  to={tournamentScheduleHref(match, calendarDate)}
                  data-publish-link
                >
                  Publicar en su calendario →
                </Link>
              </li>
            ))}
          </ul>
        </section>
      ) : null}

      <div className="schedule-calendar-card adm-courts__body">
        <div className="schedule-calendar__workspace">
          <aside className="schedule-calendar__queue adm-courts__queue" aria-label="Partidos por programar">
            <div className="adm-courts__queue-head">
              <h2>Por programar</h2>
              <span className="adm-courts__queue-count">{orderedUnscheduledMatches.length}</span>
            </div>
            <p className="schedule-calendar__order-hint">La cola reúne partidos sin programar de todos los torneos del club seleccionado.</p>
            {orderedUnscheduledMatches.length > 0 ? (
              <div className="adm-courts__queue-filters">
                <input
                  type="search"
                  className="adm-courts__queue-search"
                  aria-label="Buscar partido"
                  placeholder="Buscar jugador o torneo"
                  value={queueQuery}
                  onChange={(e) => setQueueQuery(e.target.value)}
                />
                {queueTournaments.length > 1 ? (
                  <select className="t123-select" aria-label="Torneo" value={queueTournament} onChange={(e) => setQueueTournament(e.target.value)}>
                    <option value="">Todos los torneos del club</option>
                    {queueTournaments.map(([id, name]) => (
                      <option key={id} value={id}>{name}</option>
                    ))}
                  </select>
                ) : null}
              </div>
            ) : null}
            {orderedUnscheduledMatches.length === 0 ? (
              <p className="muted schedule-calendar__empty">No hay partidos sin programar para este club/fecha.</p>
            ) : visibleQueue.length === 0 ? (
              <p className="muted schedule-calendar__empty">Ningún partido coincide con la búsqueda.</p>
            ) : (
              <div className="schedule-calendar__match-list">
                {visibleQueue.map((match) => (
                  <button type="button" key={match.id} className={`schedule-calendar__match-chip${selectedMatchId === match.id ? " is-selected" : ""}`} aria-pressed={selectedMatchId === match.id} onClick={() => handleQueueSelect(match)}>
                    <span>{matchShortLabel(match)}</span>
                    <small>{tournamentBadge(match)}</small>
                    {selectedMatchId === match.id ? <em className="adm-courts__chip-tag">Seleccionado · toca una hora libre</em> : null}
                  </button>
                ))}
              </div>
            )}
            {selectedMatch && (
              <div className="schedule-calendar__selected">
                <span className="schedule-toolbar__label">Seleccionado</span>
                <strong>{matchShortLabel(selectedMatch)}</strong>
                <small className="muted">{tournamentBadge(selectedMatch)}</small>
                {isScheduleUnpublished(selectedMatch) ? (
                  <span className="schedule-publish-tag is-unpublished">Sin publicar</span>
                ) : isActiveCalendarStatus(selectedMatch.status) && isSchedulePublished(selectedMatch) ? (
                  <span className="schedule-publish-tag is-published">Publicado · los jugadores ya lo ven</span>
                ) : null}
                <div className="schedule-calendar__order-actions">
                  <Link
                    className="btn btn--ghost btn--compact"
                    to={tournamentScheduleHref(selectedMatch, selectedMatch.scheduled_date ?? calendarDate)}
                  >
                    Ver calendario del torneo
                  </Link>
                  <button type="button" className="btn btn--ghost btn--compact" onClick={() => setSelectedMatchId(null)}>Limpiar selección</button>
                </div>
              </div>
            )}
            <div className="schedule-calendar__blocks-panel">
              <div className="schedule-calendar__queue-header"><span>Bloqueos del día</span><strong>{blocks.length}</strong></div>
              {blocks.length === 0 ? (
                <p className="muted schedule-calendar__empty">Sin horarios bloqueados.</p>
              ) : (
                <div className="schedule-calendar__block-list">
                  {blocks.map((block) => (
                    <button type="button" key={block.id} className="schedule-calendar__block-chip" onClick={() => void handleDeleteBlock(block)} disabled={calendarBusy}>
                      <strong>{courtDisplayName(block.court)} · {block.start_time}–{block.end_time}</strong>
                      <small>{block.reason || "Bloqueado"}{block.notes ? ` · ${block.notes}` : ""}</small>
                    </button>
                  ))}
                </div>
              )}
            </div>
          </aside>

          <div className="schedule-calendar__matrix-wrap adm-courts__grid">
            <div className="adm-courts__legend" aria-label="Leyenda">
              <span className="adm-courts__legend-item adm-courts__legend-item--free">Libre</span>
              <span className="adm-courts__legend-item adm-courts__legend-item--busy">Programado</span>
              <span className="adm-courts__legend-item adm-courts__legend-item--blocked">Bloqueado</span>
              <span className="adm-courts__legend-item adm-courts__legend-item--selected">Seleccionado</span>
              <span className="adm-courts__legend-item adm-courts__legend-item--nofit">No cabe / choque</span>
              {unpublishedTodayCount > 0 ? (
                <span className="adm-courts__legend-item adm-courts__legend-item--unpublished" title="Horario que los jugadores aún no ven: se publica desde el calendario de su torneo.">Sin publicar</span>
              ) : null}
            </div>
            {hasInvalidSlots || !calendarGridRange ? (
              <p className="muted">Define una hora inicial/final válida y al menos una cancha.</p>
            ) : (
              <>
                <CourtTimeGridBar
                  ref={confirmRef}
                  hint={
                    calendarInteractionMode === "block"
                      ? `Bloquear: toca una hora libre para bloquear ${calendarMatchDurationMinutes} min desde ahí, o un bloqueo para liberarlo.`
                      : calendarInteractionMode === "reserve"
                        ? `Reservar: toca una hora libre para reservar ${calendarMatchDurationMinutes} min, o una reserva para cancelarla.`
                        : "Elige un partido (de la cola o tocando su tarjeta) y luego toca la hora de inicio en una cancha libre."
                  }
                  selection={
                    calendarInteractionMode === "schedule" && selectedMatch
                      ? {
                          verb: selectedMatch.scheduled_date ? "Mover" : "Programar",
                          label: matchShortLabel(selectedMatch),
                          durationMinutes: selectedMatchDuration,
                        }
                      : null
                  }
                  proposal={proposalView}
                  canEarlier={proposalCanShift(-15)}
                  canLater={proposalCanShift(15)}
                  onNudge={handleNudge}
                  onConfirm={handleConfirmProposal}
                  onCancelProposal={() => setProposal(null)}
                  onClearSelection={() => setSelectedMatchId(null)}
                  notice={gridNotice}
                  busy={calendarBusy}
                  date={calendarDate}
                  today={todayIso()}
                  consequence={
                    selectedMatch
                      ? scheduleBarConsequence(
                          isActiveCalendarStatus(selectedMatch.status) && isSchedulePublished(selectedMatch),
                          null,
                          hasKnownPlayer(selectedMatch),
                        )
                      : null
                  }
                />
                <CourtTimeGrid
                  courts={calendarCourts.map((court) => {
                    const courtSport = courtSportByName.get(court.trim());
                    return {
                      key: court.trim(),
                      text:
                        clubHasMultipleSports && courtSport
                          ? `${courtDisplayName(court)} · ${sportLabel(courtSport)}`
                          : courtDisplayName(court),
                      label:
                        clubHasMultipleSports && courtSport ? (
                          <>
                            {courtDisplayName(court)}
                            <small>· {sportLabel(courtSport)}</small>
                          </>
                        ) : (
                          courtDisplayName(court)
                        ),
                    };
                  })}
                  range={calendarGridRange}
                  items={clubGridItems}
                  blockMinutes={calendarMatchDurationMinutes}
                  cellState={clubCellState}
                  onCellClick={handleClubCellClick}
                  onDisabledCellClick={(_court, _time, cell) => setGridNotice(cell.title)}
                  renderItem={renderClubGridItem}
                  passThroughKey={
                    calendarInteractionMode === "schedule" && selectedMatchId !== null ? `m-${selectedMatchId}` : null
                  }
                  preview={
                    proposalView
                      ? { court: proposalView.court, startMin: proposalView.startMin, endMin: proposalView.endMin }
                      : null
                  }
                  busy={calendarBusy}
                  placing={calendarInteractionMode === "schedule" && Boolean(selectedMatch)}
                  scrollKey={`${calendarDate}|${venueName}|${calendarStartTime}`}
                  scrollToMin={clubScrollToMin}
                  ariaLabel="Canchas del club por hora"
                  date={calendarDate}
                />
                <p className="adm-courts__foot">
                  Puedes empezar cada 15 min. Toca un partido programado para seleccionarlo y moverlo.
                </p>
              </>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}
